import * as Colyseus from '@colyseus/sdk';
import { Callbacks } from '@colyseus/sdk';
import type { InputPayload } from '../shared';

export type RoomEventMap = {
  stateChange: (state: any) => void;
  machineAdded: (sessionId: string, machine: any) => void;
  machineRemoved: (sessionId: string) => void;
  objectDestroyed: (data: { id: string; destroyedBy: string; type: string; x: number; z: number }) => void;
  machineKilled: (data: { victimId: string; killerId: string; victimName: string; killerName: string }) => void;
  rewardGain: (data: { crystals: number; xp: number; levelUp?: boolean; newLevel?: number }) => void;
  upgradeApplied: (data: { sessionId: string; slot: string; tierId: number | string }) => void;
  powerUpBoxBroken: (data: { id: string; type: string; x: number; z: number }) => void;
  powerUpCollected: (data: { type: string; collectorId: string; x: number; z: number }) => void;
  powerUpActivated: (data: { type: string }) => void;
  powerUpExpired: (data: { type: string }) => void;
  playerTeleported: (data: { sessionId: string; fromX: number; fromZ: number; toX: number; toZ: number }) => void;
  flagPickedUp: (data: { team: string; carrierId: string; carrierName: string }) => void;
  flagDropped:  (data: { team: string; x: number; z: number }) => void;
  flagCaptured: (data: { scoringTeam: string; carrierName: string; redScore: number; blueScore: number }) => void;
  flagReturned: (data: { team: string; returnerId: string }) => void;
  bulletSpawn: (data: { id: string; ownerId: string; x: number; z: number; vx: number; vz: number; weaponTier: number; ttl: number; bulletVariant: number }) => void;
  bulletDespawn: (data: { id: string; x: number; z: number }) => void;
  matchStarted: (data: {}) => void;
  matchEnded:   (data: { winner: string; redScore: number; blueScore: number; topKills?: number }) => void;
  error: (msg: string) => void;
};

export class ColyseusClient {
  private client: Colyseus.Client;
  private room: Colyseus.Room | null = null;
  private listeners: Partial<{ [K in keyof RoomEventMap]: RoomEventMap[K][] }> = {};
  public sessionId: string = '';

  constructor(serverUrl: string) {
    this.client = new Colyseus.Client(serverUrl);
  }

  on<K extends keyof RoomEventMap>(event: K, cb: RoomEventMap[K]) {
    if (!this.listeners[event]) this.listeners[event] = [];
    (this.listeners[event] as any[]).push(cb);
  }

  private emit<K extends keyof RoomEventMap>(event: K, ...args: Parameters<RoomEventMap[K]>) {
    const cbs = this.listeners[event] as any[];
    if (cbs) cbs.forEach(cb => cb(...args));
  }

  // ── Quick play (public matchmaking) ─────────────────────

  async joinArena(playerName: string): Promise<void> {
    await this._joinRoom('arena', playerName);
  }

  async joinCTF(playerName: string): Promise<void> {
    await this._joinRoom('ctf', playerName);
  }

  private async _joinRoom(roomName: string, playerName: string): Promise<void> {
    try {
      this.room = await this.client.joinOrCreate(roomName, { name: playerName });
      this.sessionId = this.room.sessionId;
      this._attachRoomListeners();
    } catch (e: any) {
      this.emit('error', e.message ?? 'Connection failed');
      throw e;
    }
  }

  // ── Private rooms ───────────────────────────────────────

  async createPrivate(roomType: 'arena' | 'ctf', playerName: string, team?: 'red' | 'blue'): Promise<string> {
    const opts: Record<string, unknown> = { name: playerName, isPrivate: true };
    if (team) opts.team = team;
    try {
      this.room = await this.client.create(roomType, opts);
      this.sessionId = this.room.sessionId;
      this._attachRoomListeners();
      return this.room.roomId;
    } catch (e: any) {
      this.emit('error', e.message ?? 'Failed to create room');
      throw e;
    }
  }

  async joinPrivate(roomId: string, playerName: string, team?: 'red' | 'blue'): Promise<void> {
    const opts: Record<string, unknown> = { name: playerName };
    if (team) opts.team = team;
    try {
      this.room = await this.client.joinById(roomId.toLowerCase().replace(/-/g, ''), opts);
      this.sessionId = this.room.sessionId;
      this._attachRoomListeners();
    } catch (e: any) {
      this.emit('error', e.message ?? 'Room not found');
      throw e;
    }
  }

  // ── Shared listener wiring ──────────────────────────────

  private _attachRoomListeners(): void {
    this.room!.onStateChange((state) => {
      this.emit('stateChange', state);
    });

    const callbacks = Callbacks.get(this.room as any) as any;
    callbacks.onAdd('machines', (machine: any, sessionId: string) => {
      this.emit('machineAdded', sessionId, machine);
    }, true);
    callbacks.onRemove('machines', (_machine: any, sessionId: string) => {
      this.emit('machineRemoved', sessionId);
    });

    this.room!.onMessage('objectDestroyed', (data) => this.emit('objectDestroyed', data));
    this.room!.onMessage('machineKilled', (data) => this.emit('machineKilled', data));
    this.room!.onMessage('rewardGain', (data) => this.emit('rewardGain', data));
    this.room!.onMessage('upgradeApplied', (data) => this.emit('upgradeApplied', data));
    this.room!.onMessage('powerUpBoxBroken', (data) => this.emit('powerUpBoxBroken', data));
    this.room!.onMessage('powerUpCollected', (data) => this.emit('powerUpCollected', data));
    this.room!.onMessage('powerUpActivated', (data) => this.emit('powerUpActivated', data));
    this.room!.onMessage('powerUpExpired', (data) => this.emit('powerUpExpired', data));
    this.room!.onMessage('playerTeleported', (data) => this.emit('playerTeleported', data));
    this.room!.onMessage('flagPickedUp', (data) => this.emit('flagPickedUp', data));
    this.room!.onMessage('flagDropped',  (data) => this.emit('flagDropped', data));
    this.room!.onMessage('flagCaptured', (data) => this.emit('flagCaptured', data));
    this.room!.onMessage('flagReturned', (data) => this.emit('flagReturned', data));
    this.room!.onMessage('bulletSpawn',   (data) => this.emit('bulletSpawn', data));
    this.room!.onMessage('bulletDespawn', (data) => this.emit('bulletDespawn', data));
    this.room!.onMessage('matchStarted', (data) => this.emit('matchStarted', data));
    this.room!.onMessage('matchEnded',   (data) => this.emit('matchEnded', data));

    this.room!.onLeave(() => {
      this.room = null;
    });
  }

  // ── Outgoing messages ───────────────────────────────────

  sendInput(input: InputPayload) {
    this.room?.send('input', input);
  }

  purchaseUpgrade(slot: string, tierId: number | string) {
    this.room?.send('purchaseUpgrade', { slot, tierId });
  }

  upgradeStat(stat: string, targetLevel: number) {
    this.room?.send('upgradeStat', { stat, targetLevel });
  }

  startMatch(duration?: number) {
    this.room?.send('startMatch', { duration });
  }

  switchTeam(team: 'red' | 'blue') {
    this.room?.send('switchTeam', { team });
  }

  addBot(difficulty: 'easy' | 'medium' | 'hard') {
    this.room?.send('addBot', { difficulty });
  }

  removeBot() {
    this.room?.send('removeBot', {});
  }

  devAddCrystals(amount = 1000) {
    this.room?.send('devAddCrystals', { amount });
  }

  leave() {
    this.room?.leave();
    this.room = null;
  }

  get connected() {
    return !!this.room;
  }

  get roomId(): string {
    return this.room?.roomId ?? '';
  }

  get state() {
    return this.room?.state ?? null;
  }
}
